import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { ArrowUpRight, ShoppingBag } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { isProductAvailable } from '@app-types/product';
import { Button } from '@components/common/Button';
import { Container } from '@components/common/Container';
import { Price, TaxExclusion } from '@components/product/Price';
import { ComingSoonPurchase } from '@components/product/ProductPurchase';
import { useCart } from '@context/CartContext';
import { products } from '@data/products';
import { checkoutPath } from '@services/checkout/checkoutIntent';
const software = products.filter((product) => product.category === 'software');
const services = products.filter((product) => product.category !== 'software' && !product.variants);
export const ProductShowcase = () => {
    const { t } = useTranslation();
    return (_jsx(Showcase, { id: "productos", "aria-labelledby": "showcase-title", children: _jsxs(Container, { children: [_jsxs(ShowcaseHeader, { children: [_jsxs("div", { children: [_jsxs(Eyebrow, { children: ["04 / ", t('home.showcase.eyebrow')] }), _jsx("h2", { id: "showcase-title", children: t('home.showcase.title') }), _jsx("p", { children: t('home.showcase.intro') })] }), _jsxs(CatalogLink, { to: "/products", children: [t('home.showcase.all'), _jsx(ArrowUpRight, { size: 19, "aria-hidden": "true" })] })] }), _jsxs(CatalogNote, { children: [_jsx("span", { children: t('home.showcase.catalogNote') }), _jsx("span", { children: t('home.showcase.illustrations') })] }), software.map((product) => (_jsx(ProductOffer, { product: product, featured: true }, product.id))), _jsxs(ServiceIntro, { children: [_jsx("h3", { children: t('home.showcase.servicesTitle') }), _jsx("p", { children: t('home.showcase.servicesIntro') })] }), _jsx(ServiceOffers, { children: services.map((product) => (_jsx(ProductOffer, { product: product }, product.id))) }), _jsxs(TechnologyNote, { children: [_jsx("span", { children: "AI + Technology + Care" }), _jsx("p", { children: t('home.software.ai') })] })] }) }));
};
const ProductOffer = ({ product, featured = false }) => {
    const { t } = useTranslation();
    const { addProduct } = useCart();
    const navigate = useNavigate();
    const titleId = `offer-${product.id}`;
    const available = isProductAvailable(product);
    const handleBuyNow = () => {
        navigate(checkoutPath(product));
    };
    return (_jsxs(Offer, { "$featured": featured, "aria-labelledby": titleId, children: [_jsxs(OfferHeading, { children: [_jsxs(OfferCategory, { children: [_jsx("span", { children: featured
                                    ? t('home.software.eyebrow')
                                    : t(`productsPage.categories.${product.category}`) }), featured && (_jsx(Demo, { children: t(!available ? 'availability.comingSoon' : 'home.software.demo') }))] }), featured ? (_jsx("h3", { id: titleId, children: t(product.nameKey) })) : (_jsx("h4", { id: titleId, children: t(product.nameKey) }))] }), product.image && (_jsxs(OfferImage, { children: [_jsx("img", { src: product.image, alt: t('home.showcase.imageAlt', { product: t(product.nameKey) }), width: 720, height: 720, loading: "lazy", decoding: "async" }), featured && _jsx("figcaption", { children: t('home.showcase.softwareCaption') })] })), _jsxs(OfferDescription, { children: [_jsx("p", { children: t(product.descriptionKey) }), featured && _jsx("p", { className: "purpose", children: t('home.showcase.softwarePurpose') })] }), _jsxs(Purchase, { children: [!available ? (_jsx(ComingSoonPurchase, { product: product })) : (_jsxs(_Fragment, { children: [_jsxs(OfferPrice, { children: [_jsx("span", { children: t('home.showcase.priceLabel') }), _jsx("strong", { children: _jsx(Price, { amountMinor: product.priceMinor, currency: product.currency }) }), _jsx(TaxExclusion, {})] }), _jsxs(PurchaseButtons, { children: [_jsxs(Button, { type: "button", onClick: handleBuyNow, children: [t('actions.buyNow'), _jsx(ArrowUpRight, { size: 16, "aria-hidden": "true" })] }), _jsxs(Button, { type: "button", "$variant": "secondary", onClick: () => addProduct(product.id), children: [_jsx(ShoppingBag, { size: 17, "aria-hidden": "true" }), t('actions.addToCart')] })] })] })), _jsxs(DetailsLink, { to: `/products/${product.slug}`, children: [t('actions.viewDetails'), _jsx(ArrowUpRight, { size: 17, "aria-hidden": "true" })] })] })] }));
};
const Showcase = styled.section `
  padding-block: clamp(4rem, 7vw, 7rem);
  background: ${({ theme }) => theme.colors.white};
`;
const ShowcaseHeader = styled.div `
  display: flex;
  align-items: end;
  justify-content: space-between;
  gap: 2rem;
  h2 {
    max-width: 750px;
    margin: 0;
    color: ${({ theme }) => theme.colors.navy900};
    font-size: clamp(2rem, 3.3vw, 3rem);
    font-weight: 400;
    line-height: 1.12;
    letter-spacing: -0.05em;
  }
  p:not(:first-child) {
    max-width: 720px;
    margin: 1.5rem 0 0;
    color: ${({ theme }) => theme.colors.muted};
    font-size: 1.0625rem;
    line-height: 1.75;
  }
  > a {
    flex-shrink: 0;
  }
  @media (max-width: 899px) {
    flex-direction: column;
    align-items: start;
    gap: 1rem;
  }
`;
const Eyebrow = styled.p `
  margin: 0 0 1.5rem;
  color: ${({ theme }) => theme.colors.navy900};
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.085em;
`;
const CatalogLink = styled(Link) `
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  min-height: 44px;
  color: ${({ theme }) => theme.colors.navy900};
  font-size: 0.9375rem;
  font-weight: 700;
  svg {
    flex-shrink: 0;
    transition: transform 180ms ease;
  }
  &:hover {
    text-decoration: underline;
    text-underline-offset: 5px;
  }
  &:hover svg {
    transform: translate(2px, -2px);
  }
`;
const CatalogNote = styled.div `
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 0.5rem 2rem;
  padding-block: 1rem;
  margin-top: 2rem;
  border-bottom: 2px solid ${({ theme }) => theme.colors.navy900};
  color: ${({ theme }) => theme.colors.muted};
  font-size: 0.8125rem;
  line-height: 1.6;
`;
const OfferHeading = styled.div `
  grid-area: heading;
  h3,
  h4 {
    margin: 0;
    color: ${({ theme }) => theme.colors.navy900};
    font-size: 1.4rem;
    line-height: 1.2;
    font-weight: 700;
    letter-spacing: -0.04em;
  }
`;
const OfferCategory = styled.div `
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.7rem;
  color: ${({ theme }) => theme.colors.blue600};
  font-size: 0.6875rem;
  line-height: 1.5;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;
const Demo = styled.span `
  padding: 0.2rem 0.45rem;
  color: ${({ theme }) => theme.colors.navy900};
  background: ${({ theme }) => theme.colors.mint50};
  letter-spacing: 0.02em;
  text-transform: none;
  font-size: 0.75rem;
`;
const OfferImage = styled.figure `
  grid-area: image;
  margin: 0;
  align-self: start;
  width: 100%;
  img {
    display: block;
    width: 100%;
    height: auto;
    aspect-ratio: 4 / 3;
    object-fit: cover;
    object-position: center;
  }
  figcaption {
    padding-top: 0.75rem;
    color: ${({ theme }) => theme.colors.muted};
    font-size: 0.75rem;
    line-height: 1.6;
  }
`;
const OfferDescription = styled.div `
  grid-area: description;
  p {
    margin: 0;
    color: ${({ theme }) => theme.colors.muted};
    font-size: 1rem;
    line-height: 1.75;
  }
  .purpose {
    margin-top: 1rem;
    color: ${({ theme }) => theme.colors.navy900};
  }
`;
const Purchase = styled.div `
  grid-area: purchase;
  align-self: start;
`;
const OfferPrice = styled.div `
  display: grid;
  gap: 0.3rem;
  margin-bottom: 1rem;
  > span {
    color: ${({ theme }) => theme.colors.muted};
    font-size: 0.75rem;
  }
  strong {
    color: ${({ theme }) => theme.colors.navy900};
    font-size: 1.65rem;
    font-weight: 700;
    letter-spacing: -0.04em;
    line-height: 1.4;
  }
`;
const PurchaseButtons = styled.div `
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  button {
    font-size: 0.875rem;
    padding-inline: 1rem;
  }
`;
const DetailsLink = styled(CatalogLink) `
  margin-top: 0.75rem;
  font-size: 0.875rem;
`;
const Offer = styled.article `
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr) 230px;
  grid-template-rows: min-content 1fr;
  grid-template-areas: 'image heading purchase' 'image description purchase';
  column-gap: 2.5rem;
  row-gap: 0.8rem;
  align-items: start;
  padding-block: 2.5rem;
  ${Purchase} {
    border-left: 1px solid ${({ theme }) => theme.colors.line};
    padding-left: 1.5rem;
  }
  ${PurchaseButtons} {
    flex-direction: column;
  }
  @media (min-width: 768px) and (max-width: 1099px) {
    grid-template-columns: 180px minmax(0, 1fr);
    grid-template-areas: 'image heading' 'image description' 'image purchase';
    column-gap: 2rem;
    ${Purchase} {
      border-left: 0;
      padding: 0;
      margin-top: 0.75rem;
    }
    ${PurchaseButtons} {
      flex-direction: row;
    }
  }
  @media (max-width: 767px) {
    grid-template-columns: 112px minmax(0, 1fr);
    grid-template-areas: 'image heading' 'description description' 'purchase purchase';
    gap: 1.25rem 1rem;
    padding-block: 2rem;
    ${OfferHeading} h4 {
      font-size: 1.25rem;
    }
    ${Purchase} {
      border-left: 0;
      padding: 0;
    }
    ${PurchaseButtons} {
      flex-direction: row;
    }
    ${PurchaseButtons} button {
      flex: 1 1 auto;
    }
  }
  ${({ $featured }) => $featured &&
    css `
      grid-template-columns: minmax(0, 1fr) minmax(0, 0.9fr);
      grid-template-rows: auto auto 1fr;
      grid-template-areas: 'heading image' 'description image' 'purchase image';
      column-gap: clamp(3rem, 7vw, 7rem);
      row-gap: 1.5rem;
      padding-block: 3rem 4rem;
      ${OfferHeading} h3 {
        max-width: 530px;
        font-size: clamp(2rem, 3.3vw, 3rem);
        font-weight: 400;
        line-height: 1.1;
        letter-spacing: -0.05em;
      }
      ${OfferImage} {
        max-width: 480px;
        justify-self: end;
        align-self: center;
      }
      ${Purchase} {
        padding: 0;
        border: 0;
      }
      ${PurchaseButtons} {
        flex-direction: row;
      }
      @media (min-width: 768px) and (max-width: 1099px) {
        grid-template-columns: minmax(0, 1fr) minmax(0, 0.85fr);
        grid-template-areas: 'heading image' 'description image' 'purchase purchase';
        column-gap: 2rem;
        ${OfferImage} {
          align-self: start;
        }
      }
      @media (max-width: 767px) {
        grid-template-columns: minmax(0, 1fr);
        grid-template-areas: 'heading' 'image' 'description' 'purchase';
        gap: 1.5rem;
        padding-block: 2rem 3rem;
        ${OfferImage} {
          justify-self: start;
        }
      }
    `}
`;
const ServiceIntro = styled.div `
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem 3rem;
  padding-block: 1.5rem;
  border-top: 1px solid ${({ theme }) => theme.colors.navy900};
  border-bottom: 1px solid ${({ theme }) => theme.colors.line};
  h3 {
    font-size: 1.5rem;
    font-weight: 400;
  }
  p {
    max-width: 36rem;
    margin: 0;
    color: ${({ theme }) => theme.colors.muted};
    font-size: 0.9375rem;
    line-height: 1.65;
  }
  @media (max-width: 899px) {
    flex-direction: column;
    gap: 0.75rem;
  }
`;
const ServiceOffers = styled.div `
  > article {
    border-bottom: 1px solid ${({ theme }) => theme.colors.line};
  }
`;
const TechnologyNote = styled.div `
  display: grid;
  gap: 1rem 3rem;
  margin-top: 2rem;
  > span {
    color: ${({ theme }) => theme.colors.navy900};
    font-size: 0.75rem;
    letter-spacing: 0.08em;
  }
  p {
    max-width: 620px;
    margin: 0;
    color: ${({ theme }) => theme.colors.muted};
    font-size: 0.9375rem;
    line-height: 1.75;
  }
  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;
